import { CartAddEvent } from '@theme/events';

class SurfaceCalculator extends HTMLElement {
  connectedCallback() {
    this.mode = this.dataset.mode || 'sur-mesure';
    this.currency = this.dataset.currency || 'EUR';
    this.locale = this.dataset.locale || 'fr-FR';
    this.tva = parseFloat(this.dataset.tva) || 20;
    this.productWeight = parseFloat(this.dataset.productWeight) || null;
    // Fallback prix/m² au niveau produit (pratique en coupe : toutes les largeurs
    // d'un même produit partagent le même prix/m²). La variante reste prioritaire.
    this.productPrice = parseFloat(this.dataset.productPrice) || null;

    // Pas de coupe (10 cm par défaut) + bornes longueur en mode coupe (rouleaux
    // standards : 2,00 à 30,50 mL). Hors plage → bascule sur le CTA contact, comme
    // le plafond logistique (2 000 kg par défaut).
    this.lengthStepCoupe = parseFloat(this.dataset.lengthStepCoupe) || 0.1;
    this.lengthMinCoupe = parseFloat(this.dataset.lengthMinCoupe) || 0;
    this.lengthMaxCoupe = parseFloat(this.dataset.lengthMaxCoupe) || Infinity;
    this.weightMax = parseFloat(this.dataset.weightMax) || Infinity;
    // Plafond quantité Shopify (~1 000 000 d'unités de 0,01 € en facturation
    // exacte). Au-delà → bascule sur le CTA contact, comme le plafond poids.
    this.quantityMax = parseFloat(this.dataset.quantityMax) || 999999;

    // Coordonnées contact (utilisées pour pré-remplir le mailto en cas de hors-format).
    this.contactPhone = this.dataset.contactPhone || '';
    this.contactEmail = this.dataset.contactEmail || '';

    this.variants = this.parseVariants();
    this.currentVariant = this.variants[0] || null;

    // Inputs
    this.widthInput = this.querySelector('[data-input="width"]');
    this.lengthInput = this.querySelector('[data-input="length"]');
    this.snapInfoEl = this.querySelector('[data-snap-info]');
    this.chutesNoteEl = this.querySelector('[data-chutes-note]');

    // Contact CTA (affiché en remplacement du form quand on est hors-bornes).
    this.contactCtaEl = this.querySelector('[data-contact-cta]');
    this.contactReasonEl = this.querySelector('[data-contact-reason]');
    this.contactMailEl = this.querySelector('[data-contact-mail]');

    // Variant selectors
    this.variantRadios = this.querySelectorAll('[data-variant-radio]');
    this.variantSelect = this.querySelector('[data-variant-select]');

    // Outputs
    this.outputs = {
      surfaceRaw: this.querySelector('[data-output="surface-raw"]'),
      surfaceOrdered: this.querySelector('[data-output="surface-ordered"]'),
      unitHT: this.querySelector('[data-output="unit-ht"]'),
      unitTTC: this.querySelector('[data-output="unit-ttc"]'),
      totalTTC: this.querySelector('[data-output="total-ttc"]'),
      totalHT: this.querySelector('[data-output="total-ht"]'),
      weight: this.querySelector('[data-output="weight"]'),
    };

    // Form
    this.resultEl = this.querySelector('[data-result]');
    this.form = this.querySelector('[data-form]');
    this.variantInput = this.querySelector('[data-variant-input]');
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.submitBtn = this.querySelector('[data-submit]');
    this.ctaLabel = this.querySelector('[data-cta-label]');
    this.ctaSpinner = this.querySelector('[data-cta-spinner]');
    this.ctaLoadingLabel = this.querySelector('[data-cta-loading-label]');
    this.ctaRuler = this.querySelector('[data-cta-ruler]');
    this.ctaArrow = this.querySelector('[data-cta-arrow]');
    this.properties = {
      width: this.querySelector('[data-property="width"]'),
      length: this.querySelector('[data-property="length"]'),
      surfaceRaw: this.querySelector('[data-property="surface-raw"]'),
      surfaceOrdered: this.querySelector('[data-property="surface-ordered"]'),
      weight: this.querySelector('[data-property="weight"]'),
      weightKg: this.querySelector('[data-property="weight-kg"]'),
      priceM2: this.querySelector('[data-property="price-m2"]'),
    };

    this.bindEvents();
    this.refreshUnitPriceDisplay();
    this.calculate();
  }

  parseVariants() {
    const script = this.querySelector('script[data-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (err) {
      console.error('[surface-calculator] variants JSON parse error:', err);
      return [];
    }
  }

  bindEvents() {
    // Les deux modes utilisent désormais des radios (épaisseurs en sur-mesure,
    // largeurs en coupe). Le sélecteur est uniforme.
    this.variantRadios.forEach((radio) => {
      radio.addEventListener('change', () => this.setVariantById(radio.value));
    });

    // Backward-compat : si un select traîne encore (override custom), on l'écoute.
    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', () => this.setVariantById(this.variantSelect.value));
    }

    [this.widthInput, this.lengthInput].forEach((input) => {
      if (!input) return;
      input.addEventListener('input', () => this.calculate());
    });

    if (this.mode === 'coupe' && this.lengthInput) {
      this.lengthInput.addEventListener('change', () => this.snapLengthToStep());
    }

    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }
  }

  /**
   * En mode coupe, arrondit la longueur saisie au pas (par défaut 10 cm) le
   * plus proche supérieur, puis affiche un message pédagogique discret pendant
   * 4 s. Edouard ne veut autoriser que les multiples du pas (10,30 / 10,40
   * mais pas 10,35).
   */
  /**
   * Parse une saisie utilisateur en nombre. Les claviers mobiles français
   * tapent la virgule comme séparateur décimal ("10,5") — parseFloat seul
   * s'arrêterait à 10. On normalise virgule → point et on tolère les espaces.
   */
  parseDecimal(value) {
    const normalized = String(value ?? '').trim().replace(/\s/g, '').replace(',', '.');
    return parseFloat(normalized);
  }

  snapLengthToStep() {
    if (!this.lengthInput) return;
    const raw = this.parseDecimal(this.lengthInput.value);
    if (!Number.isFinite(raw) || raw <= 0) return;

    const step = this.lengthStepCoupe;
    if (!step || step <= 0) return;

    // Tolérance flottante : si raw est déjà un multiple du pas (à 1 mm près),
    // on ne snap pas (évite le bruit visuel sur saisies déjà valides).
    const ratio = raw / step;
    if (Math.abs(ratio - Math.round(ratio)) < 0.005) return;

    const snapped = Math.ceil(ratio) * step;
    // Précision : on coupe à la décimale du pas pour éviter 10.400000001.
    const decimals = Math.max(0, (step.toString().split('.')[1] || '').length);
    const snappedFixed = parseFloat(snapped.toFixed(decimals));

    this.lengthInput.value = String(snappedFixed).replace('.', ',');
    this.showSnapMessage(`Arrondi à ${this.formatNumber(snappedFixed)} m — coupe par tranche de ${step * 100} cm`);
    this.calculate();
  }

  showSnapMessage(message) {
    if (!this.snapInfoEl) return;
    this.snapInfoEl.textContent = message;
    this.snapInfoEl.hidden = false;
    this.snapInfoEl.classList.remove('is-fading');
    clearTimeout(this._snapTimer);
    this._snapTimer = setTimeout(() => {
      this.snapInfoEl.classList.add('is-fading');
      setTimeout(() => {
        this.snapInfoEl.hidden = true;
        this.snapInfoEl.classList.remove('is-fading');
      }, 400);
    }, 4000);
  }

  setVariantById(id) {
    const variant = this.variants.find((v) => String(v.id) === String(id));
    if (!variant) return;
    this.currentVariant = variant;
    this.variantInput.value = variant.id;

    const pillSelector = this.mode === 'sur-mesure'
      ? '.surface-calculator__thickness'
      : '.surface-calculator__width-pill';
    this.querySelectorAll(pillSelector).forEach((label) => {
      label.classList.toggle('is-active', String(label.dataset.variantId) === String(variant.id));
    });

    this.refreshUnitPriceDisplay();
    this.calculate();
  }

  refreshUnitPriceDisplay() {
    if (!this.currentVariant) return;
    const unitHT = this.getPricePerM2();
    const unitTTC = unitHT * (1 + this.tva / 100);
    if (this.outputs.unitHT) this.outputs.unitHT.textContent = this.formatPrice(unitHT) + '/m²';
    if (this.outputs.unitTTC) this.outputs.unitTTC.textContent = this.formatPrice(unitTTC) + '/m²';
  }

  getEffectiveWidth() {
    if (this.mode === 'sur-mesure') {
      const width = this.parseDecimal(this.widthInput?.value);
      return Number.isFinite(width) && width > 0 ? width : 0;
    }
    // coupe : largeur depuis la variante
    return parseFloat(this.currentVariant?.width_m) || 0;
  }

  getLength() {
    const length = this.parseDecimal(this.lengthInput?.value);
    return Number.isFinite(length) && length > 0 ? length : 0;
  }

  getWeightPerM2() {
    // Priorité au metafield de la variante (ce qu'Edouard remplit en back-office),
    // fallback produit pour compatibilité avec les anciens produits.
    const variantWeight = parseFloat(this.currentVariant?.weight_per_m2);
    if (Number.isFinite(variantWeight) && variantWeight > 0) return variantWeight;
    return this.productWeight || 0;
  }

  /**
   * Facturation exacte = le vrai prix au m² est dans le metafield custom.prix_m2
   * (le champ "prix" Shopify de la variante vaut alors 0,01 €, l'unité de vente).
   * Sinon on reste sur l'ancien modèle : le champ "prix" EST le prix au m².
   * Activé produit par produit → migration progressive sans rien casser.
   */
  isExactBilling() {
    const p = parseFloat(this.currentVariant?.price_m2);
    if (Number.isFinite(p) && p > 0) return true;
    return !!(this.productPrice && this.productPrice > 0); // fallback produit (coupe)
  }

  getPricePerM2() {
    const meta = parseFloat(this.currentVariant?.price_m2);
    if (Number.isFinite(meta) && meta > 0) return meta; // mode exact, prix variante
    if (this.productPrice && this.productPrice > 0) return this.productPrice; // fallback produit
    return (this.currentVariant?.price || 0) / 100; // legacy (centimes → euros/m²)
  }

  calculate() {
    const width = this.getEffectiveWidth();
    const length = this.getLength();
    const surface = width * length;

    if (!this.currentVariant || surface <= 0 || !Number.isFinite(surface)) {
      this.resetResult();
      return;
    }

    const exact = this.isExactBilling();
    const pricePerM2 = this.getPricePerM2();
    const weightPerM2 = this.getWeightPerM2();

    // billedSurface = la surface facturée (affichage + poids) ; cartQty = ce qu'on
    // envoie réellement à Shopify dans le champ quantité.
    let billedSurface;
    let cartQty;
    let totalHT;

    if (exact) {
      // Facturation exacte : le client paie sa surface au centième de m² près.
      // L'unité de vente Shopify vaut 0,01 €, donc cartQty = total en centimes.
      // Ex. 57,82 m² × 9,51 €/m² = 549,87 € → cartQty = 54 987 (× 0,01 € = 549,87 €).
      billedSurface = Math.round(surface * 100) / 100;
      const priceCents = Math.round(pricePerM2 * 100);
      cartQty = Math.max(1, Math.round(billedSurface * priceCents));
      totalHT = cartQty / 100;
    } else {
      billedSurface = Math.max(1, Math.floor(surface));
      cartQty = billedSurface;
      totalHT = billedSurface * pricePerM2;
    }

    const totalTTC = totalHT * (1 + this.tva / 100);
    const weight = Math.ceil(billedSurface * weightPerM2);
    const billedSurfaceLabel = exact ? this.formatNumber(billedSurface) : String(billedSurface);

    if (this.outputs.surfaceRaw) this.outputs.surfaceRaw.textContent = this.formatNumber(surface);
    if (this.outputs.surfaceOrdered) this.outputs.surfaceOrdered.textContent = billedSurfaceLabel;
    if (this.outputs.totalTTC) this.outputs.totalTTC.textContent = this.formatPrice(totalTTC);
    if (this.outputs.totalHT) this.outputs.totalHT.textContent = this.formatPrice(totalHT);
    if (this.outputs.weight) this.outputs.weight.textContent = weight;
    this.updateChutesNote(exact);
    this.resultEl.hidden = false;

    const overflow = this.detectOverflow(length, weight, cartQty, exact);
    if (overflow) {
      this.showContactCta(overflow, { width, length, surface, qtyOrdered: billedSurfaceLabel, weight });
      this.quantityInput.value = 0;
      this.applyButtonState('waiting');
      return;
    }

    this.hideContactCta();

    this.quantityInput.value = cartQty;
    this.variantInput.value = this.currentVariant.id;

    if (this.properties.width) {
      this.properties.width.value = this.mode === 'sur-mesure'
        ? this.formatNumber(width) + ' m'
        : this.formatNumber(width) + ' m (largeur rouleau)';
    }
    if (this.properties.length) this.properties.length.value = this.formatNumber(length) + ' m';
    if (this.properties.surfaceRaw) this.properties.surfaceRaw.value = this.formatNumber(surface) + ' m²';
    if (this.properties.surfaceOrdered) this.properties.surfaceOrdered.value = billedSurfaceLabel + ' m²';
    if (this.properties.weight) this.properties.weight.value = weight + ' kg';
    if (this.properties.weightKg) this.properties.weightKg.value = weight;
    if (this.properties.priceM2) this.properties.priceM2.value = this.formatPrice(pricePerM2) + '/m² HT';

    // UI state
    if (this.ctaLabel) this.ctaLabel.textContent = `Ajouter ${billedSurfaceLabel} m² au panier`;
    this.applyButtonState('active');
  }

  /**
   * Note sous la surface : en mode exact on ne rogne plus rien (le client paie
   * pile sa surface) ; en legacy on garde le message "arrondi en votre faveur".
   */
  updateChutesNote(exact) {
    if (!this.chutesNoteEl) return;
    this.chutesNoteEl.textContent = exact
      ? 'Vous payez exactement votre surface, au centième de m² près.'
      : 'Arrondi au m² inférieur — on vous offre toujours un peu plus.';
  }

  resetResult() {
    this.resultEl.hidden = true;
    if (this.ctaLabel) this.ctaLabel.textContent = 'Indiquez vos dimensions';
    this.quantityInput.value = 0;
    Object.values(this.properties).forEach((input) => {
      if (input) input.value = '';
    });
    this.hideContactCta();
    this.applyButtonState('waiting');
  }

  /**
   * Retourne un objet { type, message } si la commande sort des bornes, sinon
   * null. Bornes vérifiées :
   *   - mode coupe : longueur < min (2 mL) ou > max (30,50 mL)
   *   - tous modes : poids > plafond logistique (2 000 kg)
   *   - mode exact : quantité > plafond Shopify (~1 000 000 unités de 0,01 €)
   * Au-delà : bascule sur le CTA contact qui pré-remplit un mail vers contact@etoba.fr.
   */
  detectOverflow(length, weight, cartQty, exact) {
    if (this.mode === 'coupe' && length > 0 && this.lengthMinCoupe && length < this.lengthMinCoupe) {
      return {
        type: 'length-min',
        message: `Longueur minimum ${this.formatNumber(this.lengthMinCoupe)} mètres en commande directe. En dessous, contactez-nous.`,
      };
    }
    if (this.mode === 'coupe' && Number.isFinite(this.lengthMaxCoupe) && length > this.lengthMaxCoupe) {
      return {
        type: 'length-max',
        message: `Longueur maximum ${this.formatNumber(this.lengthMaxCoupe)} mètres par commande. Au-delà, contactez-nous.`,
      };
    }
    if (weight > this.weightMax) {
      return {
        type: 'weight',
        message: `Commande trop lourde (${this.formatNumber(weight)} kg). Maximum ${this.formatNumber(this.weightMax)} kg.`,
      };
    }
    if (exact && cartQty > this.quantityMax) {
      return {
        type: 'quantity',
        message: `Commande trop importante pour le paiement en ligne. Contactez-nous pour un devis sur mesure.`,
      };
    }
    return null;
  }

  showContactCta(overflow, dims) {
    if (!this.contactCtaEl) return;
    if (this.form) this.form.hidden = true;
    this.contactCtaEl.hidden = false;
    if (this.contactReasonEl) this.contactReasonEl.textContent = overflow.message;

    if (this.contactMailEl && this.contactEmail) {
      const productName = (document.querySelector('h1')?.textContent || '').trim() || 'EPDM';
      const subject = `Devis EPDM hors-format — ${productName}`;
      const body =
        `Bonjour,\n\n` +
        `Je souhaite un devis pour une commande hors-format :\n` +
        `- Produit : ${productName}\n` +
        `- Largeur : ${this.formatNumber(dims.width)} m\n` +
        `- Longueur : ${this.formatNumber(dims.length)} m\n` +
        `- Surface : ${this.formatNumber(dims.surface)} m² (${dims.qtyOrdered} m² commandés)\n` +
        `- Poids estimé : ${dims.weight} kg\n` +
        `- Motif : ${overflow.message}\n\n` +
        `Merci.`;
      this.contactMailEl.href = `mailto:${this.contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
  }

  hideContactCta() {
    if (this.contactCtaEl) this.contactCtaEl.hidden = true;
    if (this.form) this.form.hidden = false;
  }

  /**
   * Pilote l'apparence du bouton CTA via l'attribut `hidden` (source de vérité).
   * @param {'waiting' | 'active' | 'loading'} state
   */
  applyButtonState(state) {
    if (!this.submitBtn) return;
    if (this.isSubmitting && state !== 'loading') return; // ne pas casser l'état loading en cours

    const isLoading = state === 'loading';
    const isActive = state === 'active';
    const isWaiting = state === 'waiting';

    if (this.ctaSpinner) this.ctaSpinner.hidden = !isLoading;
    if (this.ctaLoadingLabel) this.ctaLoadingLabel.hidden = !isLoading;
    if (this.ctaLabel) this.ctaLabel.hidden = isLoading;
    if (this.ctaArrow) this.ctaArrow.hidden = !isActive;
    if (this.ctaRuler) this.ctaRuler.hidden = !isWaiting;

    this.submitBtn.classList.toggle('is-loading', isLoading);
    this.submitBtn.disabled = !isActive;
    this.submitBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  async handleSubmit(event) {
    event.preventDefault();

    const qty = parseInt(this.quantityInput?.value || '0', 10);
    if (!qty || qty <= 0) return;
    if (this.isSubmitting) return;

    this.setLoading(true);

    const formData = new FormData(this.form);

    // Inscrit toutes les section IDs des cart-items-components présentes sur la page
    // afin que la réponse Shopify renvoie leur HTML mis à jour (drawer + page /cart).
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      const id = el.dataset?.sectionId;
      if (id) sectionIds.add(id);
    });
    if (sectionIds.size > 0) {
      formData.set('sections', Array.from(sectionIds).join(','));
      formData.set('sections_url', window.location.pathname);
    }

    const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add';

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      const data = await response.json();

      if (data.status) {
        this.showTransientLabel(data.message || 'Impossible d’ajouter au panier');
        return;
      }

      this.dispatchEvent(
        new CartAddEvent(undefined, 'surface-calculator', {
          source: 'surface-calculator',
          itemCount: qty,
          productId: data.product_id ? String(data.product_id) : undefined,
          variantId: data.variant_id ? String(data.variant_id) : undefined,
          sections: data.sections,
        })
      );

      this.openCartDrawer();
    } catch (err) {
      console.error('[surface-calculator] cart add fetch error:', err);
      this.showTransientLabel('Erreur réseau, réessayez');
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    this.isSubmitting = isLoading;
    if (isLoading) {
      this.applyButtonState('loading');
    } else {
      // À la fin du chargement, on rétablit l'état réel selon la quantité.
      const qty = parseInt(this.quantityInput?.value || '0', 10);
      this.applyButtonState(qty > 0 ? 'active' : 'waiting');
    }
  }

  showTransientLabel(message) {
    if (!this.ctaLabel) return;
    const original = this.ctaLabel.textContent;
    this.ctaLabel.textContent = message;
    clearTimeout(this._labelResetTimer);
    this._labelResetTimer = setTimeout(() => {
      if (this.ctaLabel) this.ctaLabel.textContent = original;
    }, 3000);
  }

  openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  formatNumber(value) {
    return new Intl.NumberFormat(this.locale, {
      maximumFractionDigits: 2,
    }).format(value);
  }

  formatPrice(value) {
    return new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency: this.currency,
      maximumFractionDigits: 2,
    }).format(value);
  }
}

if (!customElements.get('surface-calculator')) {
  customElements.define('surface-calculator', SurfaceCalculator);
}
